import { designIntentSignature } from './tool-design-contract.ts';
import type { ForgeUsageSheet } from './types/forge-usage-sheet.ts';
import { type AnyViewEnvelope, DEFAULT_VIEW_SCHEMA_VERSION } from './types/views.ts';

/**
 * Preview of the tool's result screen, drawn from the usage sheet (decision
 * Forge FDE, 01/10/2026, lot F3): the real Domino view, filled with credible
 * example data, before and after the tool is built. Example data only — the
 * sandbox test shows the real result.
 */
export interface SheetPreview {
  /** Signature of what the preview depends on: regenerated only when it changes. */
  signature: string;
  envelope: AnyViewEnvelope;
  generatedAt: string;
}

/** The preview depends on the tool's name and promised result, nothing else:
 * editing a source or a promise does not redraw the screen. */
export const sheetPreviewSignature = (sheet: ForgeUsageSheet): string =>
  designIntentSignature({ title: sheet.title, result: sheet.result });

export const isSheetPreviewFresh = (
  sheet: ForgeUsageSheet | null | undefined,
  preview: SheetPreview | null | undefined,
): boolean => Boolean(sheet && preview && preview.signature === sheetPreviewSignature(sheet));

/** Model answer: one view among those the library renders, as canonical data. */
export interface SheetPreviewDraft {
  viewType: 'data_table' | 'artifact_document' | 'metric_grid' | 'action_list' | 'chart' | 'timeline';
  title: string;
  table: { columns: Array<{ key: string; label: string }>; rows: Array<{ cells: Array<{ key: string; value: string }> }> } | null;
  document: { subject: string; body: string } | null;
  metrics: Array<{ label: string; value: string; unit: string }> | null;
  actions: Array<{ label: string; priority: 'high' | 'medium' | 'low'; context: string }> | null;
  chart: {
    kind: 'line' | 'bar' | 'stacked_bar' | 'horizontal_bar';
    categories: string[];
    series: Array<{ label: string; values: number[] }>;
    unit: string;
  } | null;
  timeline: Array<{ date: string; label: string; detail: string; kind: string }> | null;
}

/** Arranges the model's example data into the canonical view envelope. */
export const toSheetPreviewEnvelope = (draft: SheetPreviewDraft): AnyViewEnvelope => {
  switch (draft.viewType) {
    case 'artifact_document':
      return {
        view_type: 'artifact_document',
        schema_version: DEFAULT_VIEW_SCHEMA_VERSION,
        data: {
          title: draft.title,
          subject: draft.document?.subject ?? '',
          body: draft.document?.body ?? '',
          body_format: 'markdown',
          meta_items: [],
        },
      };
    case 'metric_grid':
      return {
        view_type: 'metric_grid',
        schema_version: DEFAULT_VIEW_SCHEMA_VERSION,
        data: {
          title: draft.title,
          metrics: (draft.metrics ?? []).map(({ label, value, unit }) => ({ label, value, ...(unit ? { unit } : {}) })),
        },
      };
    case 'action_list':
      return {
        view_type: 'action_list',
        schema_version: DEFAULT_VIEW_SCHEMA_VERSION,
        data: {
          title: draft.title,
          items: (draft.actions ?? []).map((action, index) => ({
            id: `preview-${index}`,
            label: action.label,
            priority: action.priority,
            status: 'pending' as const,
            ...(action.context ? { context: action.context } : {}),
          })),
        },
      };
    case 'chart':
      return {
        view_type: 'chart',
        schema_version: DEFAULT_VIEW_SCHEMA_VERSION,
        data: {
          title: draft.title,
          kind: draft.chart?.kind ?? 'bar',
          categories: draft.chart?.categories ?? [],
          series: (draft.chart?.series ?? []).map((series, index) => ({
            key: `series-${index + 1}`,
            label: series.label,
            // One value per category, as the view requires.
            values: (draft.chart?.categories ?? []).map((_, position) => series.values[position] ?? null),
          })),
          ...(draft.chart?.unit ? { unit: draft.chart.unit } : {}),
        },
      };
    case 'timeline':
      return {
        view_type: 'timeline',
        schema_version: DEFAULT_VIEW_SCHEMA_VERSION,
        data: {
          title: draft.title,
          events: (draft.timeline ?? []).map(({ date, label, detail, kind }) => ({
            date,
            label,
            ...(detail ? { detail } : {}),
            ...(kind ? { kind } : {}),
          })),
        },
      };
    case 'data_table':
    default:
      return {
        view_type: 'data_table',
        schema_version: DEFAULT_VIEW_SCHEMA_VERSION,
        data: {
          title: draft.title,
          columns: draft.table?.columns ?? [],
          rows: (draft.table?.rows ?? []).map((row, index) => ({ id: `preview-${index}`, cells: row.cells })),
        },
      };
  }
};
